import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Database, Loader2 } from 'lucide-react';

export default function Login() {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const { login } = useAuth();
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            await login(email, password);
            toast.success('Connexion réussie');
            navigate('/dashboard');
        } catch (error) {
            const message = error.response?.data?.detail || 'Erreur de connexion';
            toast.error(message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex">
            {/* Left side - Image */}
            <div 
                className="hidden lg:flex lg:w-1/2 relative bg-cover bg-center"
                style={{
                    backgroundImage: 'url(https://images.unsplash.com/photo-1533777243653-9dfe89d32ce3?crop=entropy&cs=srgb&fm=jpg&q=85)'
                }}
            >
                <div className="absolute inset-0 bg-black/80" />
                <div className="relative z-10 flex flex-col justify-end p-12">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="flex items-center justify-center w-12 h-12 rounded bg-primary/20">
                            <Database className="w-6 h-6 text-primary" />
                        </div>
                        <div>
                            <h1 className="font-heading text-3xl font-bold tracking-tight text-white">
                                AD-MASTER
                            </h1>
                            <p className="text-sm text-zinc-400">Gestion de stock DCP</p>
                        </div>
                    </div>
                    <p className="text-lg text-zinc-300 max-w-md">
                        Gérez vos disques durs DCP, suivez vos projets et contrôlez 
                        les mouvements de stock en toute simplicité.
                    </p>
                </div>
            </div>

            {/* Right side - Form */}
            <div className="flex-1 flex items-center justify-center p-6 bg-background">
                <div className="w-full max-w-md">
                    {/* Mobile logo */}
                    <div className="flex items-center gap-3 mb-8 lg:hidden">
                        <div className="flex items-center justify-center w-10 h-10 rounded bg-primary/20">
                            <Database className="w-5 h-5 text-primary" />
                        </div>
                        <h1 className="font-heading text-2xl font-bold tracking-tight">
                            AD-MASTER
                        </h1>
                    </div>

                    <Card className="border-border bg-card">
                        <CardHeader className="space-y-1">
                            <CardTitle className="font-heading text-2xl">Connexion</CardTitle>
                            <CardDescription>
                                Entrez vos identifiants pour accéder à votre compte
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div className="space-y-2">
                                    <Label htmlFor="email">Email</Label>
                                    <Input
                                        id="email"
                                        type="email"
                                        autoComplete="username"
                                        placeholder="votre@email.com"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        required
                                        data-testid="login-email-input"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="password">Mot de passe</Label>
                                    <Input
                                        id="password"
                                        type="password"
                                        autoComplete="current-password"
                                        placeholder="••••••••"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        required
                                        data-testid="login-password-input"
                                    />
                                </div>
                                <Button 
                                    type="submit" 
                                    className="w-full font-semibold uppercase tracking-wider"
                                    disabled={loading}
                                    data-testid="login-submit-button"
                                >
                                    {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                                    Se connecter
                                </Button>
                            </form>
                        </CardContent>
                    </Card>
                </div>
            </div>
        </div>
    );
}
